import {
  Component,
  OnDestroy,
  OnInit,
  Signal,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  of,
  startWith,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { AbbStepperComponent } from '@components/abb-stepper/abb-stepper.component';
import { Apollo } from 'apollo-angular';
import { AbbSwitchComponent } from '@components/abb-switch/abb-switch.component';
import { FilterOption } from '@components/abb-table/abb-table.component';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/ui/toast.service';
import { validateForm } from '@utils/validation';
import { categorySelectOptionsQuery } from '@graphql/category';
import {
  ActionSelectOptionsQuery,
  ActionSelectOptionsQueryVariables,
  AutomationsDefinitionsQuery,
  AutomationTriggerSelectOptionsQuery,
  AutomationTriggerSelectOptionsQueryVariables,
  CategorySelectOptionsQuery,
  CategorySelectOptionsQueryVariables,
  CreateAutomationMutationVariables,
  DeviceSelectOptionsWImageQuery,
  DeviceSelectOptionsWImageQueryVariables,
  EventTypeSelectOptionsQuery,
  EventTypeSelectOptionsQueryVariables,
  GroupSelectOptionsWImageQuery,
  GroupSelectOptionsWImageQueryVariables,
  MeasureSelectOptionsQuery,
  MeasureSelectOptionsQueryVariables,
  PortSelectOptionsQuery,
  PortSelectOptionsQueryVariables,
  PropertySelectOptionsWImageQuery,
  PropertySelectOptionsWImageQueryVariables,
  EntityType,
  EventTypeConfiguration,
} from 'gql/graphql';
import { deviceSelectOptionsWImageQuery } from '@graphql/asset-management';
import {
  actionSelectOptionsQuery,
  automationTriggerSelectOptionsQuery,
  eventTypeSelectOptionsQuery,
  measureSelectOptionsQuery,
  portSelectOptionsQuery,
} from '@graphql/automations';
import { CommonModule } from '@angular/common';
import { MatChipsModule } from '@angular/material/chips';
import { AbbCommonModule } from 'app/abb-common.module';
import { propertySelectOptionsWImageQuery } from '@graphql/property';
import { groupSelectOptionsWImageQuery } from '@graphql/group';
import { ImageValueCellComponent } from '@components/abb-table/abb-cells/imageValueCell';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AbbTimeInputComponent } from '@components/abb-form/abb-time-picker';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { CognitoService, UserWithID } from '@services/cognito/cognito.service';
import { AbbTooltipComponent } from '@components/abb-tooltip/abb-tooltip.component';
import { noSort } from '@utils/commons';
import {
  combineDateTimeLocale,
  formatTime,
} from '@utils/datetime';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OverlayService } from '@services/ui/overlay.service';
import { UserSettingsService } from '@services/user/user-settings.service';
import { FilterUndefinedEntriesPipe } from '@pipes/filterUndefinedEntries.pipe';
import { ApolloQueryResult } from '@apollo/client/core';
import { getErrorMessage } from '@utils/errors';
import { t } from '@utils/strings';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

type Condition = {
  entityType: EntityType | null;
  deviceCategory: FilterOption | null;
  entities: Record<string, string | null | undefined>[];
  eventType: FilterOption | undefined;
  threshold: number | undefined;
  measure: FilterOption | undefined;
};
interface IntervalForm extends Record<string, AbstractControl<any, any>> {
  fromDate: FormControl<Date | null>;
  fromTime: FormControl<string | null>;
  fromTextTime: FormControl<string | null>;
  toDate: FormControl<Date | null>;
  toTime: FormControl<string | null>;
  toTextTime: FormControl<string | null>;
}
interface WhenForm extends Record<string, AbstractControl<any, any>> {
  name: FormControl<string | null>;
  interval: FormGroup<IntervalForm>;
  days: FormControl<Array<string> | null>;
}
interface IfForm extends Record<string, AbstractControl<any, any>> {
  // name: FormControl<string | null>;
  deviceCategoryId: FormControl<string | null>;
  entityType: FormControl<EntityType | null>;
  entities: FormControl<Array<string> | null>;
  eventTypeId: FormControl<string | null>;
  measureId: FormControl<string | null>;
  threshold: FormControl<number | null>;
  delay: FormControl<number | null>;
  // alarmId: FormControl<string | null>;
  sendEvent: FormControl<boolean | null>;
  enabled: FormControl<boolean | null>;
}
interface ThenForm extends Record<string, AbstractControl<any, any>> {
  typeId: FormControl<string | null>;
  deviceId: FormControl<string | null>;
  portId: FormControl<string | null>;
  actionId: FormControl<string | null>;
}
export type AutomationForm = {
  when: FormGroup<WhenForm>;
  if: FormGroup<IfForm>;
  then: FormGroup<ThenForm>;
};

@Component({
  selector: 'abb-automation-form',
  standalone: true,
  template: `
    <abb-stepper
      [stepsControls]="stepsControls()"
      [stepsLabels]="stepsLabels()"
      [onBeforeGoNextStep]="onBeforeGoNextStep"
      [isVertical]="true"
      [isLoading]="isLoading()"
      [stepperTitle]="
        (mode() === 'edit' ? 'Edit Automation' : 'New Automation') | t
      "
      (onValidationError)="showError($event)"
      (onCancel)="cancel()"
    >
      <ng-template #stepContent let-index="index">
        <form id="automation-form" [formGroup]="form" (ngSubmit)="onSubmit()">
          @if(index === 0) {
          <div formGroupName="when" class="flex flex-col gap-3">
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Automation Name' | t }}
            </h3>
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ 'Automation Name' | t }}</mat-label>
              <input matInput formControlName="name" name="name" />
              @if(form.controls.when.get('name')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Trigger' | t }}
            </h3>
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ 'On selected period' | t }}</mat-label>
              <mat-select
                formControlName="days"
                multiple
                [panelClass]="'panel border-font-checkbox'"
              >
                <div class="w-full flex flex-row-reverse px-4 py-1">
                  <mat-checkbox
                    color="primary"
                    class="mr-1 !cursor-default border-font-checkbox no-hover-checkbox"
                    [checked]="isAllSelected()"
                    (click)="$event.stopPropagation()"
                    (change)="selectAll($event)"
                  >
                  </mat-checkbox>
                </div>
                @for (option of triggerSelectOptions$ | async; track option) {
                <mat-option
                  [value]="option.value"
                  class="flex-row-reverse selected-light-primary font-medium-label"
                  >{{
                    option.skipTranslation ? option.label : (option.label | t)
                  }}</mat-option
                >
                }
              </mat-select>
              @if(form.controls.when.get('days')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
            <div class="flex flex-col">
              @if(form.controls.when.controls.interval; as intervalCtrl) {
              <mat-form-field
                formGroupName="interval"
                class="[&_.mat-mdc-form-field-infix]:w-[100px] mat-input-element flex-1"
                subscriptSizing="dynamic"
              >
                <mat-date-range-input
                  [rangePicker]="dateRangePicker"
                  [min]="minDate"
                  [max]="maxDate"
                  class="w-fit"
                  [separator]="
                    intervalCtrl?.value?.toDate ||
                    (!intervalCtrl?.value?.fromDate &&
                      !intervalCtrl?.value?.toDate)
                      ? '–'
                      : ''
                  "
                >
                  <input
                    (ngModelChange)="
                      dateChange($event, intervalCtrl.controls.fromTime)
                    "
                    [min]="minDate"
                    [max]="maxDate"
                    matStartDate
                    [formControl]="intervalCtrl.controls.fromDate"
                    [placeholder]="'Start date' | t"
                    class="placeholder:text-abb-font/40"
                  />
                  <input
                    (ngModelChange)="
                      dateChange($event, intervalCtrl.controls.toTime)
                    "
                    [min]="minDate"
                    [max]="maxDate"
                    matEndDate
                    [formControl]="intervalCtrl.controls.toDate"
                    [placeholder]="'End date' | t"
                    class="placeholder:text-abb-font/40"
                  />
                </mat-date-range-input>
                <span
                  class="w-fit max-w-full inline-flex flex-row justify-end flex-grow text-[var(--mdc-filled-text-field-input-text-color)]"
                  matTextSuffix
                >
                  @if(!dateRangePicker.opened) {
                  <input
                    [formControl]="intervalCtrl.controls.fromTextTime"
                    type="time"
                    class="text-right bg-inherit focus:outline-none"
                    step="1"
                    [ngClass]="
                      intervalCtrl?.value?.fromDate ||
                      intervalCtrl
                        .get('fromDate')
                        ?.hasError('matDatepickerParse')
                        ? 'cursor-text'
                        : 'opacity-0 focus:opacity-100 pointer-events-none'
                    "
                    (click)="
                      intervalCtrl?.value?.fromDate ||
                      intervalCtrl
                        .get('fromDate')
                        ?.hasError('matDatepickerParse')
                        ? $event.stopPropagation()
                        : null
                    "
                  />
                  <span class="mat-date-range-input-separator">{{
                    intervalCtrl?.value?.toDate ? '–' : ''
                  }}</span>
                  <input
                    [formControl]="intervalCtrl.controls.toTextTime"
                    type="time"
                    class="text-left bg-inherit focus:outline-none"
                    step="1"
                    [ngClass]="
                      intervalCtrl?.value?.toDate ||
                      intervalCtrl.get('toDate')?.hasError('matDatepickerParse')
                        ? 'cursor-text'
                        : 'opacity-0 focus:opacity-100 pointer-events-none'
                    "
                    (click)="
                      intervalCtrl?.value?.toDate ||
                      intervalCtrl.get('toDate')?.hasError('matDatepickerParse')
                        ? $event.stopPropagation()
                        : null
                    "
                  />} @else {
                  <span
                    [ngClass]="{
                      'opacity-0 focus:opacity-100': !(
                        intervalCtrl?.value?.fromDate ||
                        intervalCtrl
                          .get('fromDate')
                          ?.hasError('matDatepickerParse')
                      )
                    }"
                  >
                    {{ intervalCtrl?.value?.fromTextTime }}
                  </span>
                  <span class="mat-date-range-input-separator">{{
                    intervalCtrl?.value?.toDate ? '–' : ''
                  }}</span>
                  <span
                    [ngClass]="{
                      'opacity-0 focus:opacity-100': !(
                        intervalCtrl?.value?.toDate ||
                        intervalCtrl
                          .get('toDate')
                          ?.hasError('matDatepickerParse')
                      )
                    }"
                  >
                    {{ intervalCtrl?.value?.toTextTime }}
                  </span>
                  }
                </span>
                <mat-datepicker-toggle
                  matIconSuffix
                  [for]="dateRangePicker"
                ></mat-datepicker-toggle>
                <mat-date-range-picker #dateRangePicker [panelClass]="'panel'">
                  <mat-datepicker-actions>
                    <div class="!flex !flex-col !w-full">
                      <div class="!w-full !flex !flex-row !justify-between">
                        @if(dateRangePicker.opened) {
                        <abb-time-input
                          class="!w-full !flex !justify-center"
                          [formControl]="intervalCtrl.controls.fromTextTime"
                        ></abb-time-input>
                        <abb-time-input
                          class="!w-full !flex !justify-center"
                          [formControl]="intervalCtrl.controls.toTextTime"
                        ></abb-time-input
                        >}
                      </div>
                      <div class="flex flex-row w-full gap-5 p-5 justify-end">
                        <abb-button
                          [variant]="'clean'"
                          matDateRangePickerCancel
                          >{{ 'cancel' | t }}</abb-button
                        >
                        <abb-button matDateRangePickerApply>{{
                          'Apply' | t
                        }}</abb-button>
                      </div>
                    </div></mat-datepicker-actions
                  >
                </mat-date-range-picker>
                @if(timezone()){
                <mat-hint
                  [align]="'end'"
                  class="inline-flex items-center text-sm text-abb-font/65"
                >
                  {{ '(' + timezone() + ')' }}
                </mat-hint>
                } @if(intervalCtrl.get('fromDate')?.hasError('required') ||
                intervalCtrl.get('fromTime')?.hasError('required') ||
                intervalCtrl.get('fromTextTime')?.hasError('required') ||
                intervalCtrl.get('toDate')?.hasError('required') ||
                intervalCtrl.get('toTime')?.hasError('required') ||
                intervalCtrl.get('toTextTime')?.hasError('required')){
                <mat-error>{{ 'This field is required' | t }}</mat-error>
                }
                @if(intervalCtrl.get('fromDate')?.hasError('matDatepickerParse')
                || intervalCtrl.get('toDate')?.hasError('matDatepickerParse')){
                <mat-error>{{ 'Invalid date' | t }}</mat-error>
                }
              </mat-form-field>
              }
            </div>
          </div>
          } @if(index === 1) {
          <div formGroupName="if" class="flex flex-col gap-3">
            <!-- <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Automation Name' | t }}
            </h3>
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ 'Name' | t }}</mat-label>
              <input matInput formControlName="name" name="name" />
              @if(form.controls.if.get('name')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field> -->
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Conditions' | t }}
            </h3>
            <!-- <h3 class="text-base font-semibold text-abb-font/85">
              {{
                'Select the type of entity you would like to create an automation for'
                  | t
              }}
            </h3> -->
            @for(condition of conditions(); track condition){
            <div
              class="flex gap-2 text-abb-font/60 items-center justify-end"
              [ngClass]="{ 'bg-neutral-100': condition.editing }"
            >
              @for(entry of condition.value | keyvalue: noSort |
              filterUndefinedEntries; track entry) { @if(entry.key ===
              'entities') {
              <abb-tooltip>
                <span class="block max-w-28 truncate" trigger>{{
                  condition.value.entityType
                }}</span>
                <ul class="list-disc pl-4 pr-2">
                  @for (item of condition.value.entities; track item) {
                  <li class="text-sm">{{ item['label'] }}</li>
                  }
                </ul>
              </abb-tooltip>
              } @else if(entry.key !== 'entityType') {
              <span
                class="truncate max-w-36"
                [matTooltip]="
                  getFilterOptionValue(entry.value) ??
                  entry.value?.toString() ??
                  ''
                "
                >{{ getFilterOptionValue(entry.value) ?? entry.value }}</span
              >
              } @if (!$first && !$last && entry.value) {
              <abb-icon name="mat/chevron_right" size="small"></abb-icon>
              } }
              <div class="mx-2 inline-flex items-center justify-end gap-3">
                <abb-button
                  id="edit"
                  variant="none"
                  class="text-inherit h-8 rounded-full"
                  [ngClass]="{
                    'text-abb-primary font-semibold': condition.editing
                  }"
                  (onclick)="editCondition($event, condition)"
                  [icon]="condition.editing ? 'mat/done' : 'mat/edit'"
                  [iconExternalSize]="'text-lg'"
                />
              </div>
            </div>
            }
            <!-- <mat-radio-group
              formControlName="entityType"
              aria-label="Select an option"
              color="primary"
              class="inline-flex"
              (change)="resetFields('entityType')"
            >
              @for(option of entityTypes(); track option.value){
              <mat-radio-button
                [value]="option.value"
                class="border-font-radio-buttons flex-1"
              >
                <label
                  class="text-base"
                  [ngClass]="{
                    'font-medium':
                      form.controls.if.controls.entityType.value ===
                      option.value
                  }"
                  >{{
                    option.skipTranslation ? option.label : (option.label | t)
                  }}</label
                > </mat-radio-button
              >}
            </mat-radio-group> -->
            @if(form.value.if?.entityType === EntityType.Device){
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ 'Type' | t }}</mat-label>
              <mat-select
                formControlName="deviceCategoryId"
                (selectionChange)="resetFields('deviceCategoryId')"
                [panelClass]="'panel'"
              >
                <mat-option [value]="null">-</mat-option>
                @for (option of categorySelectOptions$ | async; track option) {
                <mat-option [value]="option.value">{{
                  option.skipTranslation ? option.label : (option.label | t)
                }}</mat-option>
                }
              </mat-select>
              @if(form.controls.if.get('deviceCategoryId')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
            }
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ entityLabel() | t }}
            </h3>
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ entityLabel() | t }}</mat-label>
              <mat-select
                formControlName="entities"
                name="entities"
                multiple
                (selectionChange)="entitySelected()"
                [panelClass]="'panel border-font-checkbox'"
              >
                <mat-select-trigger>
                  <mat-chip-listbox>
                    @for (entityId of form.controls.if.controls.entities.value;
                    track entityId) {
                    <mat-chip
                      class="chips"
                      [removable]="true"
                      (removed)="onEntityRemoved(entityId)"
                    >
                      {{ entityWith(entityId) }}
                      <abb-icon
                        matChipRemove
                        [name]="'mat/cancel'"
                        size="small"
                      ></abb-icon>
                    </mat-chip>
                    }
                  </mat-chip-listbox>
                </mat-select-trigger>
                <input
                  class="px-4 min-h-12 outline-none w-full placeholder:text-abb-font/40 bg-transparent"
                  (keydown)="$event.stopPropagation()"
                  [formControl]="searchCtrl"
                  [placeholder]="'Search' | t"
                />
                @if (isLoadingEntities()) {
                <mat-option disabled class="flex justify-center">
                  <mat-spinner diameter="24"></mat-spinner>
                </mat-option>
                } @else if (!(entities$ | async)?.length) {
                <mat-option disabled>
                  {{ 'No options available' | t }}
                </mat-option>
                } @else { @for (option of entities$ | async; track
                option['value']) {
                <mat-option
                  class="flex-row-reverse selected-light-primary font-medium-label"
                  [value]="option['value']"
                  [ngClass]="{
                    '!hidden': !filteredEntities().includes(option['value']!)
                  }"
                >
                  <abb-image-value-cell
                    [value]="option['label']!"
                    [image]="option['image']"
                  />
                </mat-option>
                } }
              </mat-select>
              @if(form.controls.if.get('entities')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ 'Event Type' | t }}</mat-label>
              <mat-select
                formControlName="eventTypeId"
                (selectionChange)="resetFields('eventTypeId')"
                [panelClass]="'panel'"
              >
                <mat-option [value]="null">-</mat-option>
                @for (option of eventTypeSelectOptions$ | async; track option) {
                <mat-option [value]="option.value">{{
                  option.skipTranslation ? option.label : (option.label | t)
                }}</mat-option>
                }
              </mat-select>
              @if(form.controls.if.get('eventTypeId')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
            @if(showThreshold()) {
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ 'Measure' | t }}</mat-label>
              <mat-select formControlName="measureId" [panelClass]="'panel'">
                <mat-option [value]="null">-</mat-option>
                @for (option of measureSelectOptions$ | async; track option) {
                <mat-option [value]="option.value">{{
                  option.skipTranslation ? option.label : (option.label | t)
                }}</mat-option>
                }
              </mat-select>
              @if(form.controls.if.get('measureId')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
            <mat-form-field
              #threshold
              [subscriptSizing]="'dynamic'"
              color="primary"
            >
              <mat-label
                >{{ 'Threshold' | t }}
                {{
                  (threshold?._control?.focused ||
                    form.get(['if', 'threshold'])?.value) &&
                  unit()
                    ? '[' + unit() + ']'
                    : ''
                }}</mat-label
              >
              <input
                matInput
                type="number"
                formControlName="threshold"
                name="threshold"
              />
              @if(form.controls.if.get('threshold')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
            }
            <div class="flex flex-row justify-between">
              <a
                (click)="addCondition($event)"
                class="link text-abb-primary py-4 w-fit"
                [ariaDisabled]="false"
                >+ {{ 'Add another condition' | t }}</a
              >
              @if(conditions() && conditions().length > 0) {
              <a
                (click)="removeCondition($event)"
                class="link text-abb-primary py-4 w-fit"
                >{{ 'Remove' | t }}</a
              >
              }
            </div>
            @if(showTimedelay()) {
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Time delay (seconds)' | t }}
            </h3>
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ 'Delay' | t }}</mat-label>
              <input
                matInput
                type="number"
                formControlName="delay"
                name="delay"
              />
              @if(form.controls.if.get('delay')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
            }
            <!-- <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Alarm' | t }}
            </h3>
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ 'Alarm' | t }}</mat-label>
              <mat-select formControlName="alarmId" [panelClass]="'panel'">
                <mat-option [value]="null">-</mat-option>
                @for (option of alarmSelectOptions$ | async; track option) {
                <mat-option [value]="option.value">{{
                  option.skipTranslation ? option.label : (option.label | t)
                }}</mat-option>
                }
              </mat-select>
              @if(form.controls.if.get('alarmId')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field> -->
            <abb-switch
              id="send-event"
              [switchPosition]="'right'"
              class="block h-full content-center self-start py-4 font-semibold text-abb-font/85"
              labelSize="normal"
              [label]="'Send event' | t"
              formControlName="sendEvent"
            ></abb-switch>
            <abb-switch
              id="alarm-enable"
              [switchPosition]="'right'"
              class="block h-full content-center self-start py-4 font-semibold text-abb-font/85"
              labelSize="normal"
              [label]="'Enabled' | t"
              formControlName="enabled"
            ></abb-switch>
          </div>
          } @if(index === 2) {
          <div formGroupName="then" class="flex flex-col gap-3">
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Type' | t }}
            </h3>
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ 'Type' | t }}</mat-label>
              <mat-select formControlName="typeId" [panelClass]="'panel'">
                <mat-option [value]="null">-</mat-option>
                @for (option of thenCategorySelectOptions$ | async; track
                option) {
                <mat-option [value]="option.value">{{
                  option.skipTranslation ? option.label : (option.label | t)
                }}</mat-option>
                }
              </mat-select>
              @if(form.controls.then.get('typeId')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Device Details' | t }}
            </h3>
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ 'Device' | t }}</mat-label>
              <mat-select formControlName="deviceId" [panelClass]="'panel'">
                <mat-option [value]="null">-</mat-option>
                @for (option of deviceSelectOptions$ | async; track option) {
                <mat-option [value]="option.value">{{
                  option.skipTranslation ? option.label : (option.label | t)
                }}</mat-option>
                }
              </mat-select>
              @if(form.controls.then.get('deviceId')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ 'Port' | t }}</mat-label>
              <mat-select formControlName="portId" [panelClass]="'panel'">
                <mat-option [value]="null">-</mat-option>
                @for (option of portSelectOptions$ | async; track option) {
                <mat-option [value]="option.value">{{
                  option.skipTranslation ? option.label : (option.label | t)
                }}</mat-option>
                }
              </mat-select>
              @if(form.controls.then.get('portId')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
            <h3 class="text-base font-semibold text-abb-font/85">
              {{ 'Action' | t }}
            </h3>
            <mat-form-field [subscriptSizing]="'dynamic'" color="primary">
              <mat-label>{{ 'Action' | t }}</mat-label>
              <mat-select formControlName="actionId" [panelClass]="'panel'">
                <mat-option [value]="null">-</mat-option>
                @for (option of actionSelectOptions$ | async; track option) {
                <mat-option [value]="option.value">{{
                  option.skipTranslation ? option.label : (option.label | t)
                }}</mat-option>
                }
              </mat-select>
              @if(form.controls.then.get('actionId')?.hasError('required')){
              <mat-error>{{ 'This field is required' | t }}</mat-error>
              }
            </mat-form-field>
          </div>
          }
        </form>
      </ng-template>
      <ng-template #lastStepBtn>
        <abb-button
          class="w-32"
          id="automation-form-submit"
          [type]="'submit'"
          [loading]="isLoading()"
          [disabled]="form.invalid || isLoading()"
          form="automation-form"
          >{{ (mode() === 'edit' ? 'Save' : 'Create') | t }}</abb-button
        >
      </ng-template>
    </abb-stepper>
  `,
  host: {
    class: 'flex h-full',
  },
  styles: `
    ::ng-deep .mat-calendar-arrow {
    display: none !important;
    }
    ::ng-deep .mat-calendar-period-button {
    display: none !important;
    }
    ::ng-deep .mat-calendar-period-button {
    pointer-events: none;
    color: rgba(0, 0, 0, 0.26); 
    }
    ::ng-deep .mat-calendar-arrow {
    display: none; 
    }
    .selected-light-primary.mdc-list-item.mdc-list-item--selected {
      background-color: rgb(var(--abb-color-primary-light) / 0.25);
      border-top: 1px solid rgb(var(--abb-color-primary-light) / 0.4);
      border-bottom: 1px solid rgb(var(--abb-color-primary-light) / 0.4);
    }
    .font-medium-label {
      --mat-option-label-text-color: var(--abb-color-black);
      --mat-option-label-text-weight: 500;
      --mat-option-selected-state-label-text-color: var(--abb-color-black);
    }
    .link {
      cursor: pointer;
      text-decoration-line: underline;
    }
    .link[aria-disabled="true"] {
      pointer-events: none;
      color: #bababa;
    }
    .no-hover-checkbox {
      --mdc-checkbox-selected-focus-state-layer-color: var(--abb-color-white);
      --mdc-checkbox-selected-hover-state-layer-color: var(--abb-color-white);
      --mdc-checkbox-selected-pressed-state-layer-color: var(--abb-color-white);
      --mdc-checkbox-unselected-focus-state-layer-color: var(--abb-color-white);
      --mdc-checkbox-unselected-hover-state-layer-color: var(--abb-color-white);
      --mdc-checkbox-unselected-pressed-state-layer-color: var(--abb-color-white);
    }
  `,
  imports: [
    CommonModule,
    AbbCommonModule,
    TPipe,
    FilterUndefinedEntriesPipe,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    AbbStepperComponent,
    AbbSwitchComponent,
    MatRadioModule,
    ImageValueCellComponent,
    MatDatepickerModule,
    AbbTimeInputComponent,
    MatCheckboxModule,
    AbbTooltipComponent,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'controls.tabs.automations' },
  ],
})
export class AutomationFormComponent implements OnInit, OnDestroy {
  readonly noSort = noSort;
  readonly EntityType = EntityType;
  mode = input<'new' | 'edit' | 'clone'>();
  currentYear = new Date().getFullYear();
  minDate = new Date(this.currentYear, 0, 1);
  maxDate = new Date(this.currentYear, 11, 31);
  isLoading = input<boolean>(false);
  isLoadingEntities = signal<boolean>(false);
  initValues =
    input<AutomationsDefinitionsQuery['automationsDefinitions'][number]>();
  user: Signal<UserWithID | null>;
  formSubmit = output<CreateAutomationMutationVariables>();
  onCancel = output<void>();
  selectedPrefixes = new Set<string>();
  triggerSelectOptions$ = new BehaviorSubject<FilterOption[]>([]);
  categorySelectOptions$ = new BehaviorSubject<FilterOption[]>([]);
  thenCategorySelectOptions$ = new BehaviorSubject<FilterOption[]>([]);
  deviceSelectOptions$ = new BehaviorSubject<FilterOption[] | []>([]);
  eventTypeSelectOptions$ = new BehaviorSubject<FilterOption[]>([]);
  measureSelectOptions$ = new BehaviorSubject<
    (FilterOption & { unit: string })[]
  >([]);
  // alarmSelectOptions$ = new BehaviorSubject<FilterOption[]>([]);
  portSelectOptions$ = new BehaviorSubject<FilterOption[]>([]);
  actionSelectOptions$ = new BehaviorSubject<FilterOption[]>([]);

  timezone = signal<string>('');
  unit = signal<string | undefined>(undefined);
  eventTypesConfiguration$ = new BehaviorSubject<EventTypeConfiguration[]>([]);
  showThreshold = signal(true);
  showTimedelay = signal(true);

  entityTypes = signal<FilterOption[]>([
    {
      value: EntityType.Property,
      label: 'Properties',
      skipTranslation: true,
    },
    { value: EntityType.Group, label: 'Groups', skipTranslation: true },
    { value: EntityType.Device, label: 'Devices', skipTranslation: true },
  ]);
  entityLabel = signal<string | null>(null);

  searchCtrl = new FormControl<string>('');
  entities$ = new BehaviorSubject<
    Array<Record<string, string | null | undefined>>
  >([]);
  filteredEntities = signal<Array<string | null | undefined>>([]);

  form: FormGroup<AutomationForm> = new FormGroup({
    when: new FormGroup({
      name: new FormControl<string>('', [Validators.required]),
      interval: new FormGroup({
        fromDate: new FormControl<Date | null>(null, [Validators.required]),
        fromTime: new FormControl<string | null>(null, [Validators.required]),
        fromTextTime: new FormControl<string | null>(null, [
          Validators.required,
        ]),
        toDate: new FormControl<Date | null>(null, [Validators.required]),
        toTime: new FormControl<string | null>(null, [Validators.required]),
        toTextTime: new FormControl<string | null>(null, [Validators.required]),
      }),
      days: new FormControl<Array<string>>(new Array(), [Validators.required]),
    }),
    if: new FormGroup({
      // name: new FormControl<string>('', [Validators.required]),
      deviceCategoryId: new FormControl<string | null>(null, []),
      entityType: new FormControl<EntityType | null>(null, [
        Validators.required,
      ]),
      entities: new FormControl<Array<string>>(new Array(), [
        Validators.required,
      ]),
      eventTypeId: new FormControl<string | null>(null, [Validators.required]),
      measureId: new FormControl<string | null>(null),
      threshold: new FormControl<number | null>(null),
      delay: new FormControl<number | null>(null),
      // alarmId: new FormControl<string | null>(null, [Validators.required]),
      sendEvent: new FormControl<boolean>(false),
      enabled: new FormControl<boolean>(false),
    }),
    then: new FormGroup({
      typeId: new FormControl<string | null>(null, [Validators.required]),
      deviceId: new FormControl<string | null>(null, [Validators.required]),
      portId: new FormControl<string | null>(null, [Validators.required]),
      actionId: new FormControl<string | null>(null, [Validators.required]),
    }),
  });
  stepsControls = computed(() => [
    this.form.controls.when,
    this.form.controls.if,
    this.form.controls.then,
  ]);
  stepsLabels = computed(() =>
    this.form.controls
      ? Object.keys(this.form.controls)?.map((l) =>
          t(this.prefix, l, this.translate)?.toUpperCase()
        )
      : []
  );

  private prefix = inject(TRANSLATION_PREFIX);
  private subscriptions: Subscription[] = [];
  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly toast: ToastService,
    private readonly overlay: OverlayService,
    private readonly cognitoService: CognitoService,
    private readonly userService: UserSettingsService
  ) {
    this.user = cognitoService.loggedUser;
    this.timezone.set(userService.timezone());
    this.subscriptions.push(
      combineLatest([
        this.form.controls.when.controls.interval.controls.fromTextTime.valueChanges.pipe(
          startWith(
            this.form.controls.when.controls.interval.controls.fromTextTime
              .value
          )
        ),
        this.form.controls.when.controls.interval.controls.fromTime.valueChanges.pipe(
          startWith(
            this.form.controls.when.controls.interval.controls.fromTime.value
          )
        ),
      ]).subscribe(([fromTextTime, fromTime]) => {
        if (fromTextTime !== fromTime) {
          if (
            this.form.controls.when.controls.interval.controls.fromTextTime
              .dirty
          ) {
            this.form.controls.when.controls.interval.controls.fromTime.setValue(
              fromTextTime,
              { emitEvent: false }
            );
          } else {
            this.form.controls.when.controls.interval.controls.fromTextTime.setValue(
              fromTime,
              { emitEvent: false }
            );
          }
        }
      }),
      combineLatest([
        this.form.controls.when.controls.interval.controls.toTextTime.valueChanges.pipe(
          startWith(
            this.form.controls.when.controls.interval.controls.toTextTime.value
          )
        ),
        this.form.controls.when.controls.interval.controls.toTime.valueChanges.pipe(
          startWith(
            this.form.controls.when.controls.interval.controls.toTime.value
          )
        ),
      ]).subscribe(([toTextTime, toTime]) => {
        if (toTextTime !== toTime) {
          if (
            this.form.controls.when.controls.interval.controls.toTextTime.dirty
          ) {
            this.form.controls.when.controls.interval.controls.toTime.setValue(
              toTextTime,
              { emitEvent: false }
            );
          } else {
            this.form.controls.when.controls.interval.controls.toTextTime.setValue(
              toTime,
              { emitEvent: false }
            );
          }
        }
      })
    );

    this.subscriptions.push(
      this.form.controls.if.controls.entityType.valueChanges.subscribe(
        (entityType) => {
          this.entityLabel.set(
            this.entityTypes().find((t) => t['value'] === entityType)?.[
              'label'
            ] ?? ''
          );
          this.isLoadingEntities.set(true);
          if (entityType === EntityType.Device) {
            this.subscriptions.push(
              this.form.controls.if.controls.deviceCategoryId.valueChanges.subscribe(
                (deviceCategoryId) => {
                  if (deviceCategoryId) {
                    this.getEntities(entityType, {
                      if: {
                        deviceCategoryId,
                      },
                    });
                  }
                }
              )
            );
          } else {
            this.getEntities(entityType);
          }
        }
      ),
      this.form.controls.if.controls.entities.valueChanges
        .pipe(
          debounceTime(1000),
          filter((value) => !!(value && value.length > 0)),
          switchMap((entitiesIds) => {
            const entityType = this.form.value.if?.entityType;
            if (entityType) {
              return apollo.watchQuery<
                EventTypeSelectOptionsQuery,
                EventTypeSelectOptionsQueryVariables
              >({
                query: eventTypeSelectOptionsQuery,
                variables: { entityType, entitiesIds },
              }).valueChanges;
            }
            return of(null);
          })
        )
        .subscribe({
          next: (res) => {
            if (res) {
              this.eventTypeSelectOptions$.next(
                res.data.eventTypeList
                  .flatMap((e) => e.types)
                  .map((eventType) => ({
                    value: eventType.id,
                    label: eventType.name,
                    skipTranslation: false,
                  }))
              );
              this.eventTypesConfiguration$.next(res.data.eventTypeList);
              this.entitySelected();
              if (this.form.value.if?.eventTypeId) {
                this.getEntities(EntityType.Device, {
                  then: {
                    deviceCategoryId: this.form.value.then?.typeId ?? null,
                  },
                });
              }
            }
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      this.form.controls.if.controls.eventTypeId.valueChanges.subscribe(
        (eventTypeId) => {
          if (eventTypeId) {
            this.eventTypesConfiguration$
              .pipe(filter((value) => value?.length > 0))
              .subscribe((eventTypesConfiguration) => {
                this.showThreshold.set(
                  !!eventTypesConfiguration.find((e) =>
                    e.types.map((t) => t.id).includes(eventTypeId)
                  )?.threshold
                );
                this.showTimedelay.set(
                  !!eventTypesConfiguration.find((e) =>
                    e.types.map((t) => t.id).includes(eventTypeId)
                  )?.timedelay
                );
                if (this.showTimedelay()) {
                  this.form.controls.if.controls.delay.enable();
                } else {
                  this.form.controls.if.controls.delay.disable();
                }
                if (this.showThreshold()) {
                  this.form.controls.if.controls.measureId.enable();
                  this.form.controls.if.controls.threshold.enable();
                  this.subscriptions.push(
                    apollo
                      .watchQuery<
                        MeasureSelectOptionsQuery,
                        MeasureSelectOptionsQueryVariables
                      >({
                        query: measureSelectOptionsQuery,
                        variables: {
                          eventId: eventTypeId,
                          entityType: this.form.value.if?.entityType,
                          entitiesIds:
                            this.form.controls.if.controls.entities.value,
                        },
                      })
                      .valueChanges.subscribe({
                        next: ({ data }) => {
                          this.measureSelectOptions$.next(
                            data.measureList.map((measure) => ({
                              value: measure.id,
                              label: measure.name,
                              unit: measure.unit,
                              skipTranslation: false,
                            }))
                          );
                        },
                        error: (err) => {
                          //let errMsg = getErrorMessage(err, this.translate);
                          //this.toast.notifyError(errMsg);
                        },
                      }),
                    this.form.controls.if.controls.measureId.valueChanges.subscribe(
                      (v) => {
                        this.unit.set(
                          this.measureSelectOptions$.value.find(
                            (m) => m.value === v
                          )?.unit
                        );
                      }
                    )
                  );
                } else {
                  this.form.controls.if.controls.measureId.disable();
                  this.form.controls.if.controls.threshold.disable();
                }
              });
          }
        }
      ),
      apollo
        .watchQuery<
          AutomationTriggerSelectOptionsQuery,
          AutomationTriggerSelectOptionsQueryVariables
        >({ query: automationTriggerSelectOptionsQuery })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.triggerSelectOptions$.next(
              data.automationTriggerList.map((t) => ({
                value: t.id,
                label: t.name,
                skipTranslation: true,
              }))
            );
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      apollo
        .watchQuery<
          CategorySelectOptionsQuery,
          CategorySelectOptionsQueryVariables
        >({ query: categorySelectOptionsQuery })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.categorySelectOptions$.next(
              data.categoryList.map((cat) => ({
                value: cat.id,
                label: cat.name,
                skipTranslation: false,
              }))
            );

            this.thenCategorySelectOptions$.next(
              data.categoryList
                .filter((c) => c.id === '3' || c.id === '10')
                .map((cat) => ({
                  value: cat.id,
                  label: cat.name,
                  skipTranslation: false,
                }))
            );
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      // apollo
      //   .watchQuery<AlarmSelectOptionsQuery, AlarmSelectOptionsQueryVariables>({
      //     query: alarmSelectOptionsQuery,
      //   })
      //   .valueChanges.subscribe({
      //     next: ({ data }) => {
      //       this.alarmSelectOptions$.next(
      //         data.alarmList.map((alarm) => ({
      //           value: alarm.id,
      //           label: alarm.name,
      //           skipTranslation: true,
      //         }))
      //       );
      //     },
      //     error: (err) => {
      //     },
      //   }),
      this.form.controls.then.controls.typeId.valueChanges.subscribe(
        (typeId) => {
          if (typeId) {
            this.getEntities(EntityType.Device, {
              then: {
                deviceCategoryId: typeId,
              },
            });
          }
        }
      ),
      this.form.controls.then.controls.deviceId.valueChanges.subscribe(
        (deviceId) => {
          if (deviceId) {
            apollo
              .watchQuery<
                PortSelectOptionsQuery,
                PortSelectOptionsQueryVariables
              >({
                query: portSelectOptionsQuery,
                variables: {
                  devicesIds: [deviceId],
                },
              })
              .valueChanges.subscribe({
                next: ({ data }) => {
                  this.portSelectOptions$.next(
                    data.portList.map((port) => ({
                      value: port.id,
                      label: port.name,
                      skipTranslation: true,
                    }))
                  );
                },
                error: (err) => {
                  //let errMsg = getErrorMessage(err, this.translate);
                  //this.toast.notifyError(errMsg);
                },
              }),
              apollo
                .watchQuery<
                  ActionSelectOptionsQuery,
                  ActionSelectOptionsQueryVariables
                >({
                  query: actionSelectOptionsQuery,
                  variables: {
                    devicesIds: [deviceId],
                  },
                })
                .valueChanges.subscribe({
                  next: ({ data }) => {
                    this.actionSelectOptions$.next(
                      data.actionList.map((action) => ({
                        value: action.id,
                        label: action.name,
                        skipTranslation: false,
                      }))
                    );
                  },
                  error: (err) => {
                    //let errMsg = getErrorMessage(err, this.translate);
                    //this.toast.notifyError(errMsg);
                  },
                });
          }
        }
      ),
      this.searchCtrl.valueChanges.subscribe((searchQuery: string | null) => {
        this.filteredEntities.set(
          searchQuery
            ? this._filter(searchQuery)
            : this.entities$.value.map((p) => p['value']!).slice()
        );
      })
    );
  }

  conditionFields = signal([
    this.form.controls.if.controls.entityType,
    this.form.controls.if.controls.deviceCategoryId,
    this.form.controls.if.controls.entities,
    this.form.controls.if.controls.eventTypeId,
    this.form.controls.if.controls.measureId,
    this.form.controls.if.controls.threshold,
  ]);
  resetFields(changingField: string) {
    if (!this.conditions().some((c) => c.editing)) {
      if (changingField === 'entityType') {
        this.form.controls.if.controls.entities.reset(null, {
          emitEvent: false,
        });
        this.form.controls.if.controls.deviceCategoryId.reset(null, {
          emitEvent: false,
        });
        this.conditionFields()
          .filter((f) => f.enabled)
          .map((f) => f.markAsUntouched());
        this.form.controls.if.controls.deviceCategoryId.setValidators(
          this.form.value.if?.entityType === EntityType.Device
            ? [Validators.required]
            : []
        );
        this.form.controls.if.controls.deviceCategoryId.updateValueAndValidity();
        this.form.controls.if.controls.eventTypeId.reset(null, {
          emitEvent: false,
        });
        this.form.controls.if.controls.measureId.reset(null, {
          emitEvent: false,
        });
        this.form.controls.if.controls.threshold.reset(null, {
          emitEvent: false,
        });
        this.entities$.next([]);
        this.filteredEntities.set([]);
      } else if (changingField === 'deviceCategoryId') {
        this.form.controls.if.controls.entities.reset(null, {
          emitEvent: false,
        });
        this.form.controls.if.controls.eventTypeId.reset(null, {
          emitEvent: false,
        });
        this.form.controls.if.controls.measureId.reset(null, {
          emitEvent: false,
        });
        this.form.controls.if.controls.threshold.reset(null, {
          emitEvent: false,
        });
      } else if (changingField === 'eventTypeId') {
        this.form.controls.if.controls.measureId.reset();
        this.form.controls.if.controls.threshold.reset();
      }
    }
  }

  getFilterOptionValue(item: any): string | null {
    if (item && typeof item === 'object' && 'label' in item) {
      return item['label'];
    }
    return null;
  }
  private setCurrentCondition(condition: {
    value: Condition;
    editing: boolean;
  }): void {
    this.form.controls.if.patchValue({
      entityType: condition.value.entityType ?? null,
      threshold: condition.value.threshold ?? null,
    });
    this.categorySelectOptions$
      .pipe(
        filter((value) => value?.length > 0),
        take(1)
      )
      .subscribe((categorySelectOptions) => {
        this.form.controls.if.patchValue({
          deviceCategoryId:
            (categorySelectOptions.find(
              (c) => c.value === condition.value.deviceCategory?.value
            )?.value as string) ?? null,
        });
      });
    this.patchConditionSelectFormField({
      conditionEntities: condition.value.entities?.map(
        (entity: Record<string, string | null | undefined>) => entity['value']!
      ),
      conditionEventTypeId: condition.value.eventType?.value,
      conditionMeasureId: condition.value.measure?.value,
    });
  }
  private getCurrentCondition(): Condition {
    const entityType = this.form.value.if?.entityType ?? null;
    const deviceCategory =
      entityType === EntityType.Device
        ? this.categorySelectOptions$.value.find(
            (c) => c.value === this.form.value.if?.deviceCategoryId
          ) ?? null
        : null;
    const entities =
      this.entities$.value.filter(
        (a) => a['value'] && this.form.value.if?.entities?.includes(a['value'])
      ) ?? null;
    const eventType = this.eventTypeSelectOptions$.value.find(
      (e) => e.value === this.form.value.if?.eventTypeId
    );
    const measure = this.measureSelectOptions$.value.find(
      (m) => m.value === this.form.value.if?.measureId
    );
    const threshold = this.form.value.if?.threshold ?? undefined;
    return {
      entityType,
      deviceCategory,
      entities,
      eventType,
      threshold,
      measure,
    };
  }
  private restoreCurrentCondition(): void {
    this.setCurrentCondition(this.conditions()[this.conditions()?.length - 1]);
    this.conditions.update((previous) => {
      return [...previous.slice(0, -1)];
    });
  }
  conditions = model<Array<{ value: Condition; editing: boolean }>>([]);
  addCondition(e: Event): void {
    if (this.conditions().some((c) => c.editing)) {
      this.toast.notifyError(
        t(
          this.prefix,
          `Please save or cancel changes to current condition before adding a new one`,
          this.translate
        )
      );
      return;
    }
    if (
      !this.conditionFields()
        .filter((f) => f.enabled)
        .every((f) => f.valid)
    ) {
      this.conditionFields()
        .filter((f) => f.enabled)
        .map((f) => {
          f.markAsTouched();
        });
      return;
    }
    this.conditions.update((previous) => [
      ...previous,
      {
        value: this.getCurrentCondition(),
        editing: false,
      },
    ]);
    this.conditionFields().map((f) => f.reset());
    this.entities$.next([]);
    this.filteredEntities.set([]);
    this.eventTypeSelectOptions$.next([]);
    this.measureSelectOptions$.next([]);
    this.form.controls.if.controls.measureId.enable();
    this.form.controls.if.controls.threshold.enable();
    this.form.controls.if.controls.delay.enable();
    this.showThreshold.set(true);
    this.showTimedelay.set(true);
    this.form.controls.if.patchValue({ entityType: EntityType.Device });
  }
  editCondition(
    e: Event,
    condition: { value: Condition; editing: boolean }
  ): void {
    e.preventDefault();
    const editing = !condition.editing;
    if (editing && this.conditions().some((c) => c.editing)) {
      this.toast.notifyError(
        t(
          this.prefix,
          `Please save or cancel changes to current condition before editing another one`,
          this.translate
        )
      );
      return;
    }
    if (
      !this.conditionFields()
        .filter((f) => f.enabled)
        .every((f) => f.valid)
    ) {
      this.overlay
        .openAlert({
          message: `${t(
            this.prefix,
            'WARNING: This action will result in losing any unsaved changes.<br>Do you want to save changes before continuing',
            this.translate
          )}?`,
          htmlMessage: true,
          cancelText: t(this.prefix, 'No', this.translate),
          confirmText: t(this.prefix, 'Yes', this.translate),
        })
        .then(() => {
          this.conditionFields()
            .filter((f) => f.enabled)
            .map((f) => {
              f.markAsTouched();
            });
        })
        .catch(() => {
          this.conditions.update((previous) => {
            const conditionToRemoveIdx = previous.indexOf(condition);
            previous.splice(conditionToRemoveIdx, 1);
            return [...previous];
          });
          this.setCurrentCondition(condition);
        });
      return;
    }
    if (editing) {
      this.conditions.update((previous) => [
        ...previous,
        {
          value: this.getCurrentCondition(),
          editing: false,
        },
      ]);
      this.setCurrentCondition(condition);
    } else {
      condition.value = this.getCurrentCondition();
      this.restoreCurrentCondition();
    }
    condition.editing = editing;
  }
  removeCondition(
    e: Event,
    condition?: { value: Condition; editing: boolean }
  ): void {
    e.preventDefault();
    this.overlay
      .openAlert({
        message: `${t(
          this.prefix,
          'Are you sure you want to remove condition',
          this.translate
        )}?`,
        htmlMessage: true,
        confirmText: t(this.prefix, 'Remove', this.translate),
      })
      .then(() => {
        const _condition =
          condition ?? this.conditions()?.find((c) => c.editing);
        if (_condition) {
          this.conditions.update((previous) => {
            const conditionToRemoveIdx = previous.indexOf(_condition);
            previous.splice(conditionToRemoveIdx, 1);
            return [...previous];
          });
        }
        this.restoreCurrentCondition();
      });
  }

  private getEntities(
    entityType: EntityType | null,
    opts: {
      if?: { deviceCategoryId: string | null };
      then?: { deviceCategoryId: string | null };
    } = { if: { deviceCategoryId: null }, then: { deviceCategoryId: null } }
  ): void {
    if (entityType === EntityType.Property) {
      this.subscriptions.push(
        this.apollo
          .watchQuery<
            PropertySelectOptionsWImageQuery,
            PropertySelectOptionsWImageQueryVariables
          >({
            query: propertySelectOptionsWImageQuery,
          })
          .valueChanges.subscribe({
            next: ({ data }) => {
              const propertyList = data.propertyList.map((property) => ({
                value: property.id,
                label: property.name,
                image: property.imgUrl,
              }));
              this.entities$.next(propertyList);
              this.filteredEntities.set(
                propertyList.map((p) => p['value']).slice()
              );
              this.isLoadingEntities.set(false);
            },
            error: (err) => {
              //let errMsg = getErrorMessage(err, this.translate);
              //this.toast.notifyError(errMsg);
            },
          })
      );
    } else if (entityType === EntityType.Group) {
      this.subscriptions.push(
        this.apollo
          .watchQuery<
            GroupSelectOptionsWImageQuery,
            GroupSelectOptionsWImageQueryVariables
          >({
            query: groupSelectOptionsWImageQuery,
          })
          .valueChanges.subscribe({
            next: ({ data }) => {
              const groupList = data.groupList.map((group) => ({
                value: group.id,
                label: group.name,
                image: group.imgUrl,
              }));
              this.entities$.next(groupList);
              this.filteredEntities.set(
                groupList.map((g) => g['value']).slice()
              );
              this.isLoadingEntities.set(false);
            },
            error: (err) => {
              //let errMsg = getErrorMessage(err, this.translate);
              //this.toast.notifyError(errMsg);
            },
          })
      );
    }
    if (opts.then?.deviceCategoryId || opts.if?.deviceCategoryId) {
      this.subscriptions.push(
        this.apollo
          .watchQuery<
            DeviceSelectOptionsWImageQuery,
            DeviceSelectOptionsWImageQueryVariables
          >({
            query: deviceSelectOptionsWImageQuery,
            variables: {
              categoryId:
                opts.if?.deviceCategoryId ??
                opts.then?.deviceCategoryId ??
                undefined,
            },
          })
          .valueChanges.subscribe({
            next: ({ data }) => {
              if (opts.then?.deviceCategoryId) {
                this.deviceSelectOptions$.next(
                  data.deviceList.map((device) => ({
                    value: device.id,
                    label: device.name,
                    skipTranslation: true,
                  }))
                );
              } else if (opts.if?.deviceCategoryId) {
                const deviceWImgList = data.deviceList.map((device) => ({
                  value: device.id,
                  label: device.name,
                  image: device.imgUrl,
                }));
                this.entities$.next(deviceWImgList);
                this.filteredEntities.set(
                  deviceWImgList.map((d) => d['value']).slice()
                );
              }

              if (opts.then?.deviceCategoryId) {
                const rawList = data.deviceList.map((device) => ({
                  value: device.id,
                  label: device.name,
                  skipTranslation: true,
                }));

                const filteredList = this.selectedPrefixes.size
                  ? rawList.filter((device) => {
                      const prefix = device.value.split('#g')[0];
                      return this.selectedPrefixes.has(prefix);
                    })
                  : rawList;

                this.deviceSelectOptions$.next(filteredList);
                console.log(filteredList, 'filteredList');
              }

              this.isLoadingEntities.set(false);
            },
            error: (err) => {
              //let errMsg = getErrorMessage(err, this.translate);
              //this.toast.notifyError(errMsg);
            },
          })
      );
    }
  }

  ngOnInit(): void {
    if (this.mode() === 'new') {
      this.form.controls.if.controls.entityType.setValue(EntityType.Device);
      this.form.controls.if.controls.deviceCategoryId.setValidators([
        Validators.required,
      ]);
      this.form.controls.if.controls.deviceCategoryId.updateValueAndValidity();
    } else {
      const initConditions = this.initValues()?.if.conditions;

      // set summarized conditions
      this.conditions.set(
        initConditions?.slice(0, -1)?.map((c) => {
          return {
            editing: false,
            value: {
              entityType: c.observedEntities.type ?? null,
              deviceCategory: c.deviceCategory
                ? {
                    value: c.deviceCategory.id,
                    label: c.deviceCategory.name,
                  }
                : null,
              entities:
                (c.observedEntities.type === 'DEVICE'
                  ? this.initValues()?.devices
                  : c.observedEntities.type === 'GROUP'
                  ? this.initValues()?.groups
                  : this.initValues()?.properties
                )
                  ?.filter((e) => e && c.observedEntities.ids.includes(e.id))
                  ?.map((e) => ({
                    value: e?.id,
                    label: e?.name,
                  })) ?? [],
              eventType: c.eventType
                ? {
                    value: c.eventType.id,
                    label: c.eventType.name,
                  }
                : undefined,
              threshold: c.threshold ?? undefined,
              measure: c.measure
                ? {
                    value: c.measure.id,
                    label: c.measure.name,
                  }
                : undefined,
            },
          };
        }) ?? []
      );

      // set current condition form
      const initCurrentCondition = initConditions?.[initConditions?.length - 1];

      const observedEntities = initCurrentCondition?.observedEntities?.ids;
      const entityType = initCurrentCondition?.observedEntities?.type ?? null;
      const deviceCategoryId = initCurrentCondition?.deviceCategory?.id ?? null;
      const threshold = initCurrentCondition?.threshold ?? null;
      const eventTypeId = initCurrentCondition?.eventType?.id ?? undefined;
      const measureId = initCurrentCondition?.measure?.id ?? undefined;

      this.form.patchValue({
        when: {
          name: this.initValues()?.name ?? null,
          interval: {
            fromDate: this.initValues()?.when.interval.fromDate ?? null,
            fromTime: this.initValues()?.when.interval.fromDate
              ? formatTime(
                  this.initValues()?.when.interval.fromDate!,
                  this.translate.currentLang,
                  false,
                  true
                )
              : null,
            fromTextTime: this.initValues()?.when.interval.fromDate
              ? formatTime(
                  this.initValues()?.when.interval.fromDate!,
                  this.translate.currentLang,
                  false,
                  true
                )
              : null,
            toDate: this.initValues()?.when.interval.toDate ?? null,
            toTime: this.initValues()?.when.interval.toDate
              ? formatTime(
                  this.initValues()?.when.interval.toDate!,
                  this.translate.currentLang,
                  false,
                  true
                )
              : null,
            toTextTime: this.initValues()?.when.interval.toDate
              ? formatTime(
                  this.initValues()?.when.interval.toDate!,
                  this.translate.currentLang,
                  false,
                  true
                )
              : null,
          },
        },
        if: {
          name: this.initValues()?.name ?? null,
          entityType: entityType,
          threshold: threshold,
          delay: this.initValues()?.if.delay ?? null,
          sendEvent: this.initValues()?.if.sendEvent ?? null,
          enabled: this.initValues()?.enabled ?? null,
        },
      });
      this.triggerSelectOptions$
        .pipe(
          filter((value) => value?.length > 0),
          take(1)
        )
        .subscribe((triggerSelectOptions) => {
          this.form.patchValue({
            when: {
              days:
                triggerSelectOptions
                  .filter((t) =>
                    this.initValues()
                      ?.when.days?.map((d) => d.id)
                      .includes(t.value as string)
                  )
                  ?.map((d) => d.value as string) ?? [],
            },
          });
        });
      // this.alarmSelectOptions$
      //   .pipe(
      //     filter((value) => value?.length > 0),
      //     take(1)
      //   )
      //   .subscribe((alarmSelectOptions) => {
      //     this.form.patchValue({
      //       if: {
      //         alarmId:
      //           (alarmSelectOptions.find(
      //             (a) => a.value === this.initValues()?.alarm.id
      //           )?.value as string) ?? null,
      //       },
      //     });
      //   });

      this.categorySelectOptions$
        .pipe(
          filter((value) => value?.length > 0),
          take(1)
        )
        .subscribe((categorySelectOptions) => {
          this.form.patchValue({
            if: {
              deviceCategoryId:
                (categorySelectOptions.find((c) => c.value === deviceCategoryId)
                  ?.value as string) ?? null,
            },
            then: {
              typeId:
                (categorySelectOptions.find(
                  (c) => c.value === this.initValues()?.then.device.category.id
                )?.value as string) ?? null,
            },
          });
        });

      this.deviceSelectOptions$
        .pipe(
          filter((value) => value?.length > 0),
          take(1)
        )
        .subscribe((deviceSelectOptions) => {
          this.form.controls.then.patchValue({
            deviceId:
              (deviceSelectOptions.find(
                (d) => d.value === this.initValues()?.then.device.id
              )?.value as string) ?? null,
          });
        });
      this.portSelectOptions$
        .pipe(
          filter((value) => value?.length > 0),
          take(1)
        )
        .subscribe((portSelectOptions) => {
          this.form.controls.then.patchValue({
            portId:
              (portSelectOptions.find(
                (p) => p.value === this.initValues()?.then.port.id
              )?.value as string) ?? null,
          });
        });
      this.actionSelectOptions$
        .pipe(
          filter((value) => value?.length > 0),
          take(1)
        )
        .subscribe((actionSelectOptions) => {
          this.form.controls.then.patchValue({
            actionId:
              (actionSelectOptions.find(
                (a) => a.value === this.initValues()?.then.action.id
              )?.value as string) ?? null,
          });
        });

      this.patchConditionSelectFormField({
        conditionEntities: observedEntities,
        conditionEventTypeId: eventTypeId,
        conditionMeasureId: measureId,
      });
    }
  }

  private patchConditionSelectFormField(condition: {
    conditionEntities: string[] | undefined;
    conditionEventTypeId: string | number | undefined;
    conditionMeasureId: string | number | undefined;
  }): void {
    let entitiesSub: Subscription;
    entitiesSub = this.entities$
      .pipe(filter((value) => value?.length > 0))
      .subscribe((entities) => {
        this.form.controls.if.patchValue({
          entities:
            entities
              .filter((e) =>
                condition.conditionEntities?.includes(e['value'] as string)
              )
              ?.map((d) => d['value'] as string) ?? null,
        });
        entitiesSub?.unsubscribe();
      });
    let eventTypeSelectOptionsSub: Subscription;
    eventTypeSelectOptionsSub = this.eventTypeSelectOptions$
      .pipe(filter((value) => value?.length > 0))
      .subscribe((eventTypeSelectOptions) => {
        this.form.controls.if.patchValue({
          eventTypeId:
            (eventTypeSelectOptions.find(
              (e) => e.value === condition.conditionEventTypeId
            )?.value as string) ?? null,
        });
        eventTypeSelectOptionsSub?.unsubscribe();
      });
    let measureSelectOptionsSub: Subscription;
    measureSelectOptionsSub = this.measureSelectOptions$
      .pipe(filter((value) => value?.length > 0))
      .subscribe((measureSelectOptions) => {
        this.form.controls.if.patchValue({
          measureId:
            (measureSelectOptions.find(
              (m) => m.value === condition.conditionMeasureId
            )?.value as string) ?? null,
        });
        measureSelectOptionsSub?.unsubscribe();
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  cancel() {
    this.onCancel.emit();
  }

  onBeforeGoNextStep = (): Promise<boolean> => {
    const conditions = [
      ...(this.conditions() ?? []).map((c) => c.value),
      this.getCurrentCondition(),
    ];
    const mappedConditions = conditions.map((c) => JSON.stringify(c));
    return new Promise((resolve, reject) => {
      if (this.conditions()?.some((c) => c.editing)) {
        this.toast.notifyError(
          t(
            this.prefix,
            `Please save or cancel changes to current condition before leaving this step`,
            this.translate
          )
        );
        return resolve(false);
      } else if (new Set(mappedConditions).size !== mappedConditions.length) {
        this.toast.notifyError(
          t(
            this.prefix,
            `Please remove duplicated conditions before proceeding`,
            this.translate
          )
        );
        return resolve(false);
      }
      resolve(true);
    });
  };

  showError(e: any) {
    if (e.containsRequired) {
      this.toast.notifyError(
        t(
          this.prefix,
          `Please fill in all required fields before moving forward`,
          this.translate
        )
      );
    } else if (e.containsInvalid) {
      this.toast.notifyError(
        t(this.prefix, `Some fields are not valid`, this.translate)
      );
    }
  }

  onSubmit(): void {
    const formGroup = this.form.controls.then;
    validateForm(formGroup, (error) => {
      this.showError({
        containsRequired: error.requiredError,
        containsInvalid: error.invalidError,
      });
    });
    if (this.form.invalid) {
      return;
    }
    const automationData = {
      when: {
        name: this.form.value.when?.name!,
        interval: {
          fromDate: combineDateTimeLocale(
            this.form.value.when?.interval?.fromDate!,
            this.form.value.when?.interval?.fromTime ?? ''
          ),
          toDate: combineDateTimeLocale(
            this.form.value.when?.interval?.toDate!,
            this.form.value.when?.interval?.toTime ?? ''
          ),
        },
        days: this.form.value.when?.days ?? [],
      },
      if: {
        // name: this.form.value.if?.name!,
        conditions: [
          ...this.conditions()?.map((c) => c.value),
          this.getCurrentCondition(),
        ].map((c) => ({
          deviceCategoryId: c.deviceCategory
            ? c.deviceCategory.value.toString()
            : null,
          observedEntities: {
            ids:
              c.entities?.map(
                (entity: Record<string, string | null | undefined>) =>
                  entity['value']!
              ) ?? [],
            type: c.entityType!,
          },
          eventTypeId: c.eventType?.value?.toString()!,
          threshold: c.threshold ?? undefined,
          measureId: c.measure ? c.measure.value.toString() : undefined,
        })),
        delay: this.form.value.if?.delay!,
        // alarmId: this.form.value.if?.alarmId!,
        sendEvent: this.form.value.if?.sendEvent!,
        enabled: this.form.value.if?.enabled!,
      },
      then: {
        typeId: this.form.value.then?.typeId!,
        deviceId: this.form.value.then?.deviceId!,
        portId: this.form.value.then?.portId!,
        actionId: this.form.value.then?.actionId!,
      },
    };

    console.log(automationData, 'data form');

    this.formSubmit.emit(automationData);
  }

  entityWith(entityId: string) {
    return this.entities$.value.find((p) => p['value'] === entityId)?.['label'];
  }

  entitySelected(): void {
    this.searchCtrl.setValue(null);
    const selectedEntities: string[] =
      this.form.controls.if.controls.entities.value || [];
    this.selectedPrefixes.clear();

    for (const id of selectedEntities) {
      const prefix = this.extractPrefix(id);
      if (prefix) this.selectedPrefixes.add(prefix);
    }

    const allEntities = this.entities$.getValue();
    const filteredIds = allEntities
      .filter((entity) => {
        const value = entity['value'];
        const prefix = value ? this.extractPrefix(value) : null;
        // se non ci sono prefissi selezionati, mostra tutto
        if (this.selectedPrefixes.size === 0) return true;
        return prefix !== null && this.selectedPrefixes.has(prefix);
      })
      .map((entity) => entity['value']);

    this.filteredEntities.set(filteredIds);

    console.log(this.filteredEntities());
  }

  private extractPrefix(id: string): string | null {
    // Es: da "d#22e1b864_acea_48a5_8e21_9a8e418ae24a#g4" → "d#22e1b864_acea_48a5_8e21_9a8e418ae24a"
    const parts = id.split('#');
    if (parts.length >= 2) {
      return `${parts[0]}#${parts[1]}`; // "d#UUID"
    }
    return null;
  }

  onEntityRemoved(option: string) {
    const entitiesCtrl = this.form.controls.if.controls.entities;
    const entities = entitiesCtrl.value as Array<string>;

    this.removeFirst(entities, option);
    entitiesCtrl.setValue(entities);

    // Ricalcola i prefissi attivi
    const activePrefixes = entities
      .map((id) => this.extractPrefix(id))
      .filter((prefix): prefix is string => !!prefix);

    // Aggiorna l'elenco dei dispositivi filtrati
    if (activePrefixes.length > 0) {
      const allEntities = this.entities$.getValue();
      const filtered = allEntities
        .map((e) => e['value'])
        .filter((id) =>
          activePrefixes.some((prefix) => id?.startsWith(prefix))
        );
      this.filteredEntities.set(filtered);
    } else {
      // Se nessun prefisso attivo, mostra tutto
      const allEntities = this.entities$.getValue();
      this.filteredEntities.set(allEntities.map((e) => e['value']));
    }
  }

  private removeFirst<T>(array: T[], toRemove: T): void {
    const index = array.indexOf(toRemove);
    if (index !== -1) {
      array.splice(index, 1);
    }
  }

  private _filter(searchQuery: string): Array<string> {
    return this.entities$.value
      .filter((p) =>
        p['label']!.toLowerCase().trim().includes(searchQuery.toLowerCase())
      )
      .map((p) => p['value']!);
  }

  selectAll(e: MatCheckboxChange): void {
    const newValue = e.checked;
    this.triggerSelectOptions$
      .pipe(
        filter((value) => value?.length > 0),
        take(1)
      )
      .subscribe((triggerSelectOptions) => {
        this.form.controls.when.patchValue({
          days: newValue
            ? triggerSelectOptions.map((o) => o.value?.toString())
            : [],
        });
      });
  }

  isAllSelected() {
    const numSelected = this.form.value.when?.days?.length;
    const numRows = this.triggerSelectOptions$.value.length;
    return numSelected != 0 && numSelected === numRows;
  }

  dateChange(e: Date, ctrl: FormControl) {
    if (!e) {
      ctrl.reset();
    }
  }
}
